// CategoricalMatrixVisualization - 2x2 matrix for categorical frameworks (Eisenhower, etc.)
// Implements US-5.2: 2x2 Matrix Visualizations (Eisenhower with categorical selection)
// NOTE: Generic component for any categorical matrix framework

import React, { useMemo } from 'react';
import { Item, Framework } from '../../types/domain';
import styles from './MatrixVisualization.module.css';

interface CategoricalMatrixVisualizationProps {
  items: Item[];
  framework: Framework;
}

/**
 * Quadrant configuration for categorical matrix frameworks
 */
interface QuadrantConfig {
  id: string;
  label: string;
  description: string;
  color: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  criteria: { [key: string]: string }; // Maps criterion name to required value
}

/**
 * Item with calculated quadrant
 */
interface CategoricalMatrixItem {
  id: string;
  title: string;
  quadrant: string;
  criteria: { [key: string]: string };
}

/**
 * GET quadrant configurations for Eisenhower framework
 * NOTE: Can be extended for other categorical matrix frameworks
 */
const getQuadrantConfigs = (frameworkSlug: string): QuadrantConfig[] => {
  if (frameworkSlug === 'eisenhower') {
    return [
      {
        id: 'do-first',
        label: 'Do First',
        description: 'Dringend & Wichtig',
        color: '#ef4444', // Red
        position: 'top-right',
        criteria: { urgency: 'urgent', importance: 'important' },
      },
      {
        id: 'schedule',
        label: 'Schedule',
        description: 'Nicht dringend, Wichtig',
        color: '#10b981', // Green
        position: 'top-left',
        criteria: { urgency: 'not-urgent', importance: 'important' },
      },
      {
        id: 'delegate',
        label: 'Delegate',
        description: 'Dringend, Nicht wichtig',
        color: '#eab308', // Yellow
        position: 'bottom-right',
        criteria: { urgency: 'urgent', importance: 'not-important' },
      },
      {
        id: 'eliminate',
        label: 'Eliminate',
        description: 'Nicht dringend, Nicht wichtig',
        color: '#6b7280', // Gray
        position: 'bottom-left',
        criteria: { urgency: 'not-urgent', importance: 'not-important' },
      },
    ];
  }
  return [];
};

/**
 * GET axis labels for framework
 */
const getAxisLabels = (frameworkSlug: string) => {
  if (frameworkSlug === 'eisenhower') {
    return {
      xAxis: 'Dringlichkeit',
      xAxisLow: 'Nicht dringend',
      xAxisHigh: 'Dringend',
      yAxis: 'Wichtigkeit',
      yAxisLow: 'Nicht wichtig',
      yAxisHigh: 'Wichtig',
    };
  }
  return {
    xAxis: '',
    xAxisLow: '',
    xAxisHigh: '',
    yAxis: '',
    yAxisLow: '',
    yAxisHigh: '',
  };
};

/**
 * DETERMINE which quadrant an item belongs to
 * NOTE: Direct categorical matching (no thresholds)
 */
const determineQuadrant = (
  itemCriteria: { [key: string]: string },
  quadrantConfigs: QuadrantConfig[]
): string | null => {
  // FIND quadrant where all criteria match
  for (const config of quadrantConfigs) {
    const allMatch = Object.entries(config.criteria).every(
      ([key, value]) => itemCriteria[key] === value
    );
    if (allMatch) {
      return config.label;
    }
  }
  return null;
};

/**
 * CATEGORICAL MATRIX VISUALIZATION component
 * - Displays 2x2 matrix for categorical frameworks (e.g., Eisenhower)
 * - Groups items into quadrants based on categorical values
 * - Generic and reusable for any categorical matrix framework
 */
export const CategoricalMatrixVisualization: React.FC<CategoricalMatrixVisualizationProps> = ({
  items,
  framework,
}) => {
  /**
   * PROCESS items and group by quadrant
   */
  const { matrixItems, unscoredItems, quadrants } = useMemo(() => {
    const quadrantConfigs = getQuadrantConfigs(framework.slug);
    const scored: CategoricalMatrixItem[] = [];
    const unscored: Item[] = [];

    // GET criteria names from framework
    const criteriaNames = framework.criteria?.criteriaList?.map((c: any) => c.id) || [];

    // PROCESS each item
    items.forEach((item) => {
      // EXTRACT categorical values for all criteria
      const itemCriteria: { [key: string]: string } = {};
      let allScored = true;

      criteriaNames.forEach((criterionName: string) => {
        const scoreValue = String(item.scores?.find((s) => s.criterion === criterionName)?.value || '');
        if (scoreValue) {
          itemCriteria[criterionName] = scoreValue;
        } else {
          allScored = false;
        }
      });

      // VALIDATE: Only process if all criteria are scored
      if (allScored && Object.keys(itemCriteria).length === criteriaNames.length) {
        const quadrant = determineQuadrant(itemCriteria, quadrantConfigs);
        if (quadrant) {
          scored.push({
            id: item.id,
            title: item.title,
            quadrant,
            criteria: itemCriteria,
          });
        } else {
          unscored.push(item);
        }
      } else {
        unscored.push(item);
      }
    });

    // GROUP items by quadrant
    const quadrantMap = quadrantConfigs.map((config) => ({
      ...config,
      items: scored.filter((item) => item.quadrant === config.label),
    }));

    return {
      matrixItems: scored,
      unscoredItems: unscored,
      quadrants: quadrantMap,
    };
  }, [items, framework]);

  const axisLabels = getAxisLabels(framework.slug);

  // RENDER empty state if no items are scored
  if (matrixItems.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateIcon}>📊</div>
        <h3>Füge Themen / Elemente und Bewertungen hinzu, um Ergebnisse zu sehen</h3>
        <p>Bewerte mindestens ein Thema / Element, um die Matrix-Visualisierung zu sehen</p>
      </div>
    );
  }

  // ORGANIZE quadrants by position for grid layout
  const topLeft = quadrants.find((q) => q.position === 'top-left');
  const topRight = quadrants.find((q) => q.position === 'top-right');
  const bottomLeft = quadrants.find((q) => q.position === 'bottom-left');
  const bottomRight = quadrants.find((q) => q.position === 'bottom-right');

  return (
    <div className={styles.container}>
      {/* MATRIX SECTION */}
      <div className={styles.matrixWrapper}>
        {/* Y-AXIS LABEL (Vertical) */}
        <div className={styles.yAxisLabel}>
          <div className={styles.yAxisText}>{axisLabels.yAxis}</div>
          <div className={styles.yAxisHigh}>{axisLabels.yAxisHigh}</div>
          <div className={styles.yAxisLow}>{axisLabels.yAxisLow}</div>
        </div>

        {/* MATRIX GRID */}
        <div className={styles.matrixContainer}>
          <div className={styles.matrixGrid}>
            {/* TOP LEFT QUADRANT */}
            {topLeft && (
              <div
                className={styles.quadrant}
                style={{ borderColor: topLeft.color }}
                data-position="top-left"
              >
                <div className={styles.quadrantHeader} style={{ backgroundColor: topLeft.color }}>
                  <h4>{topLeft.label}</h4>
                  <p>{topLeft.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {topLeft.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    topLeft.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* TOP RIGHT QUADRANT */}
            {topRight && (
              <div
                className={styles.quadrant}
                style={{ borderColor: topRight.color }}
                data-position="top-right"
              >
                <div className={styles.quadrantHeader} style={{ backgroundColor: topRight.color }}>
                  <h4>{topRight.label}</h4>
                  <p>{topRight.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {topRight.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    topRight.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BOTTOM LEFT QUADRANT */}
            {bottomLeft && (
              <div
                className={styles.quadrant}
                style={{ borderColor: bottomLeft.color }}
                data-position="bottom-left"
              >
                <div
                  className={styles.quadrantHeader}
                  style={{ backgroundColor: bottomLeft.color }}
                >
                  <h4>{bottomLeft.label}</h4>
                  <p>{bottomLeft.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {bottomLeft.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    bottomLeft.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* BOTTOM RIGHT QUADRANT */}
            {bottomRight && (
              <div
                className={styles.quadrant}
                style={{ borderColor: bottomRight.color }}
                data-position="bottom-right"
              >
                <div
                  className={styles.quadrantHeader}
                  style={{ backgroundColor: bottomRight.color }}
                >
                  <h4>{bottomRight.label}</h4>
                  <p>{bottomRight.description}</p>
                </div>
                <div className={styles.quadrantContent}>
                  {bottomRight.items.length === 0 ? (
                    <div className={styles.emptyQuadrant}>Keine Themen / Elemente</div>
                  ) : (
                    bottomRight.items.map((item) => (
                      <div key={item.id} className={styles.itemCard} title={item.title}>
                        <div className={styles.itemTitle}>{item.title}</div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* X-AXIS LABELS (Horizontal) */}
          <div className={styles.xAxisLabels}>
            <span className={styles.xAxisLow}>{axisLabels.xAxisLow}</span>
            <span className={styles.xAxisCenter}>{axisLabels.xAxis}</span>
            <span className={styles.xAxisHigh}>{axisLabels.xAxisHigh}</span>
          </div>
        </div>
      </div>

      {/* UNSCORED ITEMS - Show at bottom if any */}
      {unscoredItems.length > 0 && (
        <div className={styles.unscoredSection}>
          <h4 className={styles.unscoredTitle}>Nicht bewertete Themen / Elemente ({unscoredItems.length})</h4>
          <div className={styles.unscoredList}>
            {unscoredItems.map((item) => (
              <div key={item.id} className={styles.unscoredItem}>
                <span className={styles.unscoredIcon}>○</span>
                <span className={styles.unscoredItemTitle}>{item.title}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
